import { useState, useEffect, useMemo } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  TextInput,
  Checkbox,
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Table,
  Badge,
  Collapse,
  Textarea,
  ActionIcon,
  Stepper,
  Loader,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { useCanOrder } from './shared/useCanOrder'
import { useSharedPanels } from './shared/useSharedPanels'
import {
  loadCustomPanels,
  createCustomPanel,
  updateCustomPanel,
  deleteCustomPanel,
  PANEL_VENDOR_LABCORP,
} from './customPanelStorage'
import type { CustomTestPanel } from './customPanelStorage'
import { CustomPanelModal } from './CustomPanelModal'
import { CustomPanelsSection } from './CustomPanelsSection'
import {
  LABCORP_QUESTIONNAIRE_ID,
  LABCORP_INSTRUCTION_CODES,
  LABCORP_MICROBIOLOGY_SOURCES,
  createInitialLabcorpFormState,
} from './labcorpFormData'
import type { LabcorpFormState, LabcorpWriteInTest } from './labcorpFormData'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { getErrorMessage, logError, logWarning } from './shared/errorUtils'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapLabcorpToPdf } from './pdf/mapLabcorpToPdf'
import { loadLogoDataUrl } from './shared/loadLogoDataUrl'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { OrderReviewSummary } from './shared/OrderReviewSummary'
import type { ReviewSection } from './shared/OrderReviewSummary'
import { LabFacilityStep } from './LabFacilityStep'
import type { Facility } from './shared/facilityTypes'
import { useTestCatalog } from './shared/useTestCatalog'
import type { CatalogTest } from './shared/useTestCatalog'
import { useSaveDraftCoordinatorFlow } from './shared/useSaveDraftCoordinatorFlow'
import { draftOnlyReason, SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'

type Props = {
  patient: Patient
  onBack: () => void
}

type Step = 'tests' | 'facility' | 'details' | 'review'
const STEPS: Step[] = ['tests', 'facility', 'details', 'review']

/** Simple sections rendered via the dynamic loop */
const LABCORP_SIMPLE_SECTIONS: { category: string; label: string; description?: string }[] = [
  {
    category: 'organ-disease-panels',
    label: 'ORGAN / DISEASE PANELS',
    description: 'See reverse for components.',
  },
  { category: 'hematology', label: 'HEMATOLOGY' },
  { category: 'enhanced-reporting', label: 'ENHANCED REPORTING' },
]

export const LabcorpRequisitionForm = ({ patient, onBack }: Props) => {
  const [step, setStep] = useState<Step>('tests')
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null)
  // PD-1312 — "Patient will choose a Labcorp location": no branch is selected
  // and nothing is faxed; the patient carries a printable requisition valid at
  // any Labcorp location. Mutually exclusive with `selectedFacility`.
  const [patientChoosesLocation, setPatientChoosesLocation] = useState(false)
  // PD-1929 — fax delivery chosen before a facility is picked. Kept here, not in the
  // delivery step, so going Back to change the tests doesn't forget it (Greptile !2060).
  const [faxDeliveryChosen, setFaxDeliveryChosen] = useState(false)

  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
  } = useAutoPopulate(patient)
  const signature = useSignature()
  const medplum = useMedplum()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  // Test catalog — fetch ALL LabCorp tests (only ~130 total)
  const { tests: allTests, loading: testsLoading } = useTestCatalog('labcorp', { _count: 500 })

  // Shared, practice-wide panels — read-only, owned by the organization.
  const {
    panels: sharedPanels,
    testLookup: sharedTestLookup,
    loading: sharedPanelsLoading,
  } = useSharedPanels(PANEL_VENDOR_LABCORP)

  // Group tests by category
  const testsByCategory = useMemo(() => {
    const map = new Map<string, CatalogTest[]>()
    for (const test of allTests) {
      const cat = test.category || 'other'
      if (!map.has(cat)) map.set(cat, [])
      map.get(cat)!.push(test)
    }
    return map
  }, [allTests])

  // Test lookup map for cart display, PDF, and custom panels. Shared-panel tests
  // are merged in so the cart and panel rows show names for codes outside the
  // loaded catalog slice; the catalog wins on conflict as the richer record.
  const testMap = useMemo(() => {
    const map = new Map<string, CatalogTest>()
    for (const [code, t] of sharedTestLookup) {
      map.set(code, {
        id: code,
        code,
        name: t.name,
        specimen: t.specimen,
        category: t.category,
        categoryDisplay: t.categoryDisplay,
      })
    }
    for (const t of allTests) map.set(t.code, t)
    return map
  }, [allTests, sharedTestLookup])

  // Group 'general' category tests into alphabetical ranges for UI
  const alphabeticalGroups = useMemo(() => {
    const generalTests = testsByCategory.get('general') || []
    const groups: { category: string; tests: CatalogTest[] }[] = [
      { category: 'A – C', tests: [] },
      { category: 'D – H', tests: [] },
      { category: 'I – P', tests: [] },
      { category: 'R – Z', tests: [] },
    ]
    for (const test of generalTests) {
      const first = test.name.charAt(0).toUpperCase()
      if (first <= 'C') groups[0].tests.push(test)
      else if (first <= 'H') groups[1].tests.push(test)
      else if (first <= 'P') groups[2].tests.push(test)
      else groups[3].tests.push(test)
    }
    return groups.filter((g) => g.tests.length > 0)
  }, [testsByCategory])

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const form = useForm<LabcorpFormState>({
    initialValues: createInitialLabcorpFormState(),
    validateInputOnBlur: true,
  })

  useEffect(() => {
    if (providerDefaults.organizationName) {
      form.setValues({
        organizationName: providerDefaults.organizationName,
        orgPhone: providerDefaults.orgPhone,
        orgFax: providerDefaults.orgFax,
        orgAddress: providerDefaults.orgAddress,
        orgCity: providerDefaults.orgCity,
        orgState: providerDefaults.orgState,
        orgZip: providerDefaults.orgZip,
      })
    }
  }, [providerDefaults.organizationName]) // eslint-disable-line react-hooks/exhaustive-deps

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: LABCORP_QUESTIONNAIRE_ID,
    generatePdfBytes: async () => {
      const logoDataUrl = selectedFacility?.logoUrl
        ? await loadLogoDataUrl(medplum, selectedFacility.logoUrl)
        : undefined
      return generateBeHumanOrderPdf(
        mapLabcorpToPdf(
          form.values,
          autoPopulateData,
          selectedFacility ?? undefined,
          testMap,
          logoDataUrl,
          patientChoosesLocation,
        ),
      )
    },
    buildFileName: () =>
      `Labcorp_Order_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () =>
      ({
        ...form.values,
        performerOrganizationId: selectedFacility?.id,
        // PD-1312 — tells middleware this requisition is patient-carried, so the
        // PDF is stamped `LabRequisition` and surfaces in the patient portal instead
        // of being hidden there as `Order`. The chart shows it under Orders only
        // (PD-1745): Documents excludes the code so the order is not listed twice.
        patientChoosesLocation,
      }) as unknown as Record<string, unknown>,
    vendorName: 'Labcorp',
    laboratoryOrganizationName: 'Labcorp',
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
    overrideFaxNumber: selectedFacility ? selectedFacility.fax || '' : null,
    // PD-1312 — nothing is transmitted when the patient picks the location, so
    // skip the fax lookup (which would otherwise block on "no fax available")
    // and the /order/send-fax call. /order/create still runs.
    skipFax: patientChoosesLocation,
  })

  // Facility draft/submit logic
  const facilityPendingApproval =
    selectedFacility && 'pendingApproval' in selectedFacility && selectedFacility.pendingApproval
  const noFacilitySelected = !selectedFacility
  // PD-1312 — a patient-chosen location has no facility by design, so none of
  // the facility gates apply and the order can be submitted outright.
  const mustSaveAsDraft =
    !patientChoosesLocation &&
    (noFacilitySelected || (selectedFacility && !selectedFacility.fax) || !!facilityPendingApproval)

  const stepIndex = STEPS.indexOf(step)
  // PD-1929 — a faxed order needs the fax number before Details, not a draft at Review.
  const canAdvanceFromFacility =
    patientChoosesLocation || (!!form.values.selectedFacilityId && !!selectedFacility?.fax)

  const handleBack = () => {
    const idx = STEPS.indexOf(step)
    if (idx === 0) {
      onBack()
    } else {
      setStep(STEPS[idx - 1])
    }
  }

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.signatureDataUrl) {
      form.setFieldValue('signatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'signatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  // Custom panels
  const [customPanels, setCustomPanels] = useState<CustomTestPanel[]>([])
  const [customPanelsLoading, setCustomPanelsLoading] = useState(true)
  const [panelModalOpen, setPanelModalOpen] = useState(false)
  const [editingPanel, setEditingPanel] = useState<CustomTestPanel | null>(null)
  const [panelSaving, setPanelSaving] = useState(false)

  useEffect(() => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    setCustomPanelsLoading(true)
    medplum
      .readResource('Practitioner', profile.id)
      .then((fresh) => setCustomPanels(loadCustomPanels(medplum, fresh, PANEL_VENDOR_LABCORP)))
      .catch((err) => logError('Custom panel load', err))
      .finally(() => setCustomPanelsLoading(false))
  }, [medplum])

  const handleToggleCustomPanel = (testCodes: string[]) => {
    const current = form.values.selectedTests
    const allSelected = testCodes.every((code) => current.includes(code))
    if (allSelected) {
      form.setFieldValue(
        'selectedTests',
        current.filter((code) => !testCodes.includes(code)),
      )
    } else {
      const newCodes = testCodes.filter((code) => !current.includes(code))
      form.setFieldValue('selectedTests', [...current, ...newCodes])
    }
  }

  const handleCreatePanel = async (name: string, testCodes: string[]) => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    setPanelSaving(true)
    try {
      const updated = await createCustomPanel(
        medplum,
        profile.id,
        name,
        testCodes,
        PANEL_VENDOR_LABCORP,
      )
      setCustomPanels(updated)
      setPanelModalOpen(false)
      setEditingPanel(null)
      notifications.show({
        title: 'Panel Created',
        message: `"${name}" has been saved.`,
        color: 'green',
      })
    } catch (err) {
      logError('Custom panel create', err)
      notifications.show({
        title: 'Failed to create panel',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setPanelSaving(false)
    }
  }

  const handleUpdatePanel = async (panelId: string, name: string, testCodes: string[]) => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    setPanelSaving(true)
    try {
      const updated = await updateCustomPanel(
        medplum,
        profile.id,
        panelId,
        { name, testCodes },
        PANEL_VENDOR_LABCORP,
      )
      setCustomPanels(updated)
      setPanelModalOpen(false)
      setEditingPanel(null)
      notifications.show({
        title: 'Panel Updated',
        message: `"${name}" has been saved.`,
        color: 'green',
      })
    } catch (err) {
      logError('Custom panel update', err)
      notifications.show({
        title: 'Failed to update panel',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setPanelSaving(false)
    }
  }

  /**
   * Deleting a panel is destructive, so it asks the way the rest of the app asks —
   * a styled modal naming the panel, not the browser's own confirm() (PD-1586).
   */
  const handleDeletePanel = (panelId: string) => {
    const panel = customPanels.find((candidate) => candidate.id === panelId)
    modals.openConfirmModal({
      title: panel ? `Delete “${panel.name}”?` : 'Delete this panel?',
      children: (
        <Text size="sm">
          This removes the panel from My Test Panels. Tests you have already selected stay on the
          form.
        </Text>
      ),
      labels: { confirm: 'Delete panel', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => void deletePanel(panelId),
    })
  }

  const deletePanel = async (panelId: string) => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    try {
      const updated = await deleteCustomPanel(medplum, profile.id, panelId, PANEL_VENDOR_LABCORP)
      setCustomPanels(updated)
      notifications.show({
        title: 'Panel Deleted',
        message: 'The panel has been removed.',
        color: 'blue',
      })
    } catch (err) {
      logError('Custom panel delete', err)
      notifications.show({
        title: 'Failed to delete panel',
        message: getErrorMessage(err),
        color: 'red',
      })
    }
  }

  // UI state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [testSearch, setTestSearch] = useState('')

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  // Toggle a test code in the unified selectedTests array
  const toggleTest = (code: string) => {
    const current = form.values.selectedTests
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    form.setFieldValue('selectedTests', next)
  }

  // NuSwab "check only one" toggle
  const toggleNuSwab = (code: string) => {
    const current = form.values.selectedTests
    const nuswabCodes = (testsByCategory.get('nuswab') || []).map((t) => t.code)
    if (current.includes(code)) {
      form.setFieldValue(
        'selectedTests',
        current.filter((c) => c !== code),
      )
    } else {
      const withoutNuSwab = current.filter((c) => !nuswabCodes.includes(c))
      form.setFieldValue('selectedTests', [...withoutNuSwab, code])
    }
  }

  // Toggle for non-test string arrays (instruction codes, microbiology sources)
  const toggleArrayItem = (field: keyof LabcorpFormState, value: string) => {
    const current = form.values[field] as unknown as string[]
    const next = current.includes(value) ? current.filter((c) => c !== value) : [...current, value]
    form.setFieldValue(field as never, next as never)
  }

  // Write-in tests
  const addWriteInTest = () => {
    form.setFieldValue('writeInTests', [
      ...form.values.writeInTests,
      { testNumber: '', testName: '' },
    ])
  }
  const updateWriteInTest = (index: number, field: keyof LabcorpWriteInTest, value: string) => {
    form.setFieldValue(
      'writeInTests',
      form.values.writeInTests.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
    )
  }
  const removeWriteInTest = (index: number) => {
    form.setFieldValue(
      'writeInTests',
      form.values.writeInTests.filter((_, i) => i !== index),
    )
  }

  // Microbiology select-all
  const microTests = testsByCategory.get('microbiology') || []
  const microCodes = microTests.map((t) => t.code)
  const allMicroSelected =
    microCodes.length > 0 && microCodes.every((c) => form.values.selectedTests.includes(c))
  const someMicroSelected =
    !allMicroSelected && microCodes.some((c) => form.values.selectedTests.includes(c))

  const toggleAllMicro = (checked: boolean) => {
    const current = form.values.selectedTests
    if (checked) {
      const newCodes = microCodes.filter((c) => !current.includes(c))
      form.setFieldValue('selectedTests', [...current, ...newCodes])
    } else {
      form.setFieldValue(
        'selectedTests',
        current.filter((c) => !microCodes.includes(c)),
      )
    }
  }

  // Filter alphabetical tests by search
  const filteredAlphabeticalGroups = useMemo(() => {
    if (!testSearch.trim()) return alphabeticalGroups
    const q = testSearch.toLowerCase()
    return alphabeticalGroups
      .map((group) => ({
        ...group,
        tests: group.tests.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.code.toLowerCase().includes(q) ||
            (t.specimen || '').toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.tests.length > 0)
  }, [testSearch, alphabeticalGroups])

  // Auto-expand categories matching search
  useEffect(() => {
    if (!testSearch.trim()) return
    setExpandedSections((prev) => {
      const next = new Set(prev)
      for (const group of filteredAlphabeticalGroups) {
        next.add(group.category)
      }
      return next
    })
  }, [testSearch, filteredAlphabeticalGroups])

  const totalSelectedCount =
    form.values.selectedTests.length +
    form.values.writeInTests.filter((t) => t.testNumber || t.testName).length

  // Cart items — grouped by category from the test map
  const cartItems: CartItem[] = useMemo(() => {
    const items: CartItem[] = []
    for (const code of form.values.selectedTests) {
      const test = testMap.get(code)
      items.push({
        id: `${test?.categoryDisplay || 'Test'}::${code}`,
        code,
        name: test?.name || code,
        category: test?.categoryDisplay || 'Test',
      })
    }
    for (const wt of form.values.writeInTests) {
      if (wt.testNumber || wt.testName) {
        items.push({
          id: `Write-In::${wt.testNumber || wt.testName}`,
          code: wt.testNumber,
          name: wt.testName || wt.testNumber,
          category: 'Write-In',
        })
      }
    }
    return items
  }, [form.values.selectedTests, form.values.writeInTests, testMap])

  const handleCartRemove = (id: string) => {
    const [category, ...rest] = id.split('::')
    const code = rest.join('::')
    if (category === 'Write-In') {
      form.setFieldValue(
        'writeInTests',
        form.values.writeInTests.filter((wt) => (wt.testNumber || wt.testName) !== code),
      )
    } else {
      form.setFieldValue(
        'selectedTests',
        form.values.selectedTests.filter((c) => c !== code),
      )
    }
  }

  const reviewSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = []
    const selectedInstructionLabels = LABCORP_INSTRUCTION_CODES.filter((ic) =>
      form.values.selectedInstructionCodes.includes(ic.code),
    ).map((ic) => ic.name)
    if (selectedInstructionLabels.length > 0) {
      sections.push({
        title: 'Instruction Codes',
        items: [{ label: 'Selected', value: selectedInstructionLabels.join(', ') }],
      })
    }
    const collection = [
      { label: 'Collection Date', value: form.values.collectionDate },
      {
        label: 'Collection Time',
        value: form.values.collectionTime
          ? `${form.values.collectionTime} ${form.values.collectionAmPm}`
          : '',
      },
      { label: 'Fasting', value: form.values.isFasting ? 'Yes' : '' },
    ]
    if (collection.some((i) => i.value)) {
      sections.push({ title: 'Collection Details', items: collection })
    }
    if (
      form.values.selectedTests.some((code) => testMap.get(code)?.category === 'microbiology') &&
      form.values.microbiologySources.length > 0
    ) {
      sections.push({
        title: 'Microbiology',
        items: [
          { label: 'Sources', value: form.values.microbiologySources.join(', ') },
          { label: 'Other Source', value: form.values.microbiologyOtherSource },
        ],
      })
    }
    if (form.values.clinicalComments) {
      sections.push({
        title: 'Clinical Comments',
        items: [{ label: 'Comments', value: form.values.clinicalComments }],
      })
    }
    return sections
  }, [form.values, testMap])

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('signatureDataUrl', dataUrl)
    form.setFieldValue('signatureDate', date)
    signature.closeSignatureModal()
  }

  const handleSubmitOrder = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before submitting.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before submitting.',
        color: 'red',
      })
      return
    }
    if (totalSelectedCount === 0) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before submitting.',
        color: 'red',
      })
      return
    }
    if (mustSaveAsDraft) {
      const reason = facilityPendingApproval ? 'facility pending approval' : 'facility has no fax'
      logWarning('Cannot submit order', `Practitioner blocked: ${reason}`)
      notifications.show({
        title: 'Cannot Submit',
        message: facilityPendingApproval
          ? 'This facility is pending admin approval. Use "Save as Draft" instead.'
          : 'The selected facility has no fax number on file. Use "Save as Draft" instead.',
        color: 'red',
      })
      return
    }
    await submission.handleSubmitOrder()
  }

  const coordinatorFlow = useSaveDraftCoordinatorFlow({
    patient,
    orderType: 'Labcorp',
    skipFacility: false,
    coordinatorNote: '',
    alreadySaved: submission.orderSubmitted,
    submitting: submission.submitting,
    onSaveDraft: submission.handleSaveDraft,
  })

  const handleSaveDraft = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before saving.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before saving.',
        color: 'red',
      })
      return
    }
    await coordinatorFlow.triggerSaveDraft()
  }

  const handleReviewOrder = () => {
    if (totalSelectedCount === 0) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before reviewing.',
        color: 'red',
      })
      return
    }
    setStep('review')
  }

  // Reusable test table renderer with specimen badges
  const renderTestTable = (tests: CatalogTest[]) => {
    const hasSpecimen = tests.some((t) => t.specimen)
    return (
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40} />
            <Table.Th w={80}>Code</Table.Th>
            <Table.Th>Test Name</Table.Th>
            {hasSpecimen && <Table.Th w={90}>Specimen</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tests.map((test) => (
            <Table.Tr key={test.code}>
              <Table.Td>
                <Checkbox
                  checked={form.values.selectedTests.includes(test.code)}
                  onChange={() => toggleTest(test.code)}
                />
              </Table.Td>
              <Table.Td>
                <Text size="xs" className="font-mono">
                  {test.code}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs" fw={500}>
                  {test.name}
                </Text>
              </Table.Td>
              {hasSpecimen && (
                <Table.Td>
                  <Badge size="xs" variant="light" color="gray">
                    {test.specimen ?? '—'}
                  </Badge>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  // Collapsible section header
  const renderSectionToggle = (label: string, count: number) => (
    <Button
      variant="subtle"
      size="sm"
      fullWidth
      justify="space-between"
      rightSection={
        expandedSections.has(label) ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />
      }
      onClick={() => toggleSection(label)}
      className="!text-left"
    >
      <Group gap="xs">
        <Text fw={600} size="sm">
          {label}
        </Text>
        <Badge size="xs" variant="light">
          {count} tests
        </Badge>
      </Group>
    </Button>
  )

  return (
    <OrderFormLayout
      cart={<OrderCart items={cartItems} onRemove={handleCartRemove} vendorLabel="Labcorp" />}
      header={
        <>
          <FormHeader
            title="Labcorp General Requisition"
            subtitle="Select how the patient will use the lab order"
            onBack={handleBack}
            onPreview={submission.handlePreviewPdf}
            onSubmit={handleSubmitOrder}
            exporting={submission.exporting}
            submitting={submission.submitting}
            orderSubmitted={submission.orderSubmitted}
            canSubmit={isPractitioner && step === 'review' && !mustSaveAsDraft}
            canOrderLoading={canOrderLoading}
            hideActions
            submitTooltip={
              !isPractitioner
                ? 'Practitioner permissions required to submit orders'
                : step !== 'review'
                  ? 'Complete all steps to submit'
                  : undefined
            }
            labError={submission.labError}
            orgError={orgError}
            warnings={insuranceWarning ? [insuranceWarning] : []}
          />
          <Stepper active={stepIndex} mb="lg" size="sm">
            <Stepper.Step label="Tests" description="Select lab tests" />
            <Stepper.Step label="Order delivery" description="Patient or fax" />
            <Stepper.Step label="Details" description="Clinical info & signature" />
            <Stepper.Step label="Review" description="Review & submit" />
          </Stepper>
        </>
      }
    >
      {/* ===== STEP 1: TESTS ===== */}
      {step === 'tests' && (
        <Stack gap="lg">
          {testsLoading && (
            <Group justify="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading test catalog...
              </Text>
            </Group>
          )}

          {!testsLoading && (
            <>
              {/* ===== INSTRUCTION CODES ===== */}
              <Paper p="md" withBorder>
                <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                  INSTRUCTION CODES
                </Text>
                <Text size="xs" c="dimmed" mb="sm">
                  Select applicable order instructions.
                </Text>
                <Group gap="md">
                  {LABCORP_INSTRUCTION_CODES.map((ic) => (
                    <Checkbox
                      key={ic.code}
                      label={ic.name}
                      checked={form.values.selectedInstructionCodes.includes(ic.code)}
                      onChange={() => toggleArrayItem('selectedInstructionCodes', ic.code)}
                      color={ic.code === '998074' ? 'red' : undefined}
                    />
                  ))}
                  <Checkbox
                    label="PST/PSC"
                    checked={form.values.isPstPsc}
                    onChange={(e) => form.setFieldValue('isPstPsc', e.target.checked)}
                  />
                </Group>
              </Paper>

              {/* ===== PRACTICE PANELS (shared, read-only) ===== */}
              <Paper p="md" withBorder>
                <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                  PRACTICE PANELS
                </Text>
                <Text size="xs" c="dimmed" mb="sm">
                  Shared test groups set up for your practice. Selecting a panel auto-checks its
                  individual tests.
                </Text>
                <CustomPanelsSection
                  readOnly
                  panels={sharedPanels}
                  selectedTests={form.values.selectedTests}
                  testLookup={testMap}
                  onTogglePanel={handleToggleCustomPanel}
                  onCreatePanel={() => {}}
                  onEditPanel={() => {}}
                  onDeletePanel={() => {}}
                  loading={sharedPanelsLoading}
                />
              </Paper>

              {/* ===== MY TEST PANELS ===== */}
              <Paper p="md" withBorder>
                <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                  MY TEST PANELS
                </Text>
                <Text size="xs" c="dimmed" mb="sm">
                  Your saved custom test groups. Selecting a panel auto-checks its individual tests.
                </Text>
                <CustomPanelsSection
                  panels={customPanels}
                  selectedTests={form.values.selectedTests}
                  testLookup={testMap}
                  onTogglePanel={handleToggleCustomPanel}
                  onCreatePanel={() => {
                    setEditingPanel(null)
                    setPanelModalOpen(true)
                  }}
                  onEditPanel={(panel) => {
                    setEditingPanel(panel)
                    setPanelModalOpen(true)
                  }}
                  onDeletePanel={handleDeletePanel}
                  loading={customPanelsLoading}
                />
              </Paper>

              <CustomPanelModal
                opened={panelModalOpen}
                onClose={() => {
                  setPanelModalOpen(false)
                  setEditingPanel(null)
                }}
                onSave={
                  editingPanel
                    ? (name, codes) => handleUpdatePanel(editingPanel.id, name, codes)
                    : handleCreatePanel
                }
                existingPanel={editingPanel}
                saving={panelSaving}
                testGroups={alphabeticalGroups}
              />

              {/* ===== DYNAMIC SIMPLE SECTIONS (organ panels, hematology, enhanced reporting) ===== */}
              {LABCORP_SIMPLE_SECTIONS.map((section) => {
                const sectionTests = testsByCategory.get(section.category) || []
                if (sectionTests.length === 0) return null
                return (
                  <Paper p="md" withBorder key={section.category}>
                    <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                      {section.label}
                    </Text>
                    {section.description && (
                      <Text size="xs" c="dimmed" mb="sm">
                        {section.description}
                      </Text>
                    )}
                    {renderSectionToggle(section.label, sectionTests.length)}
                    <Collapse in={expandedSections.has(section.label)}>
                      <div className="mr-4 ml-4">{renderTestTable(sectionTests)}</div>
                    </Collapse>
                  </Paper>
                )
              })}

              {/* ===== ALPHABETICAL / COMBINATION TESTS ===== */}
              {alphabeticalGroups.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    ALPHABETICAL / COMBINATION TESTS
                  </Text>
                  <TextInput
                    placeholder="Search by test name, code, or specimen..."
                    leftSection={<IconSearch size={16} />}
                    value={testSearch}
                    onChange={(e) => setTestSearch(e.target.value)}
                    mb="sm"
                    rightSection={
                      testSearch ? (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          aria-label="Clear search"
                          onClick={() => setTestSearch('')}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      ) : null
                    }
                    rightSectionPointerEvents={testSearch ? 'all' : 'none'}
                  />
                  <Stack gap="xs">
                    {filteredAlphabeticalGroups.map((group) => (
                      <div key={group.category}>
                        {renderSectionToggle(group.category, group.tests.length)}
                        <Collapse in={expandedSections.has(group.category)}>
                          <div className="mr-4 ml-4">{renderTestTable(group.tests)}</div>
                        </Collapse>
                      </div>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* ===== OTHER TESTS (WRITE-IN) ===== */}
              <Paper p="md" withBorder>
                <Group justify="space-between" mb="sm">
                  <Text fw={600} size="lg" className="text-[#6B21A8]">
                    OTHER TESTS / INDIVIDUAL PROFILE COMPONENTS
                  </Text>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={addWriteInTest}
                  >
                    Add Test
                  </Button>
                </Group>
                <Text size="xs" c="dimmed" mb="sm">
                  Enter test numbers and names not listed above.
                </Text>
                {form.values.writeInTests.length === 0 && (
                  <Text size="sm" c="dimmed" ta="center" py="sm">
                    No write-in tests added. Click &quot;Add Test&quot; to add one.
                  </Text>
                )}
                <Stack gap="xs">
                  {form.values.writeInTests.map((test, idx) => (
                    <Group key={idx} gap="sm" wrap="nowrap">
                      <TextInput
                        placeholder="Test #"
                        value={test.testNumber}
                        onChange={(e) => updateWriteInTest(idx, 'testNumber', e.target.value)}
                        w={120}
                      />
                      <TextInput
                        placeholder="Test Name"
                        value={test.testName}
                        onChange={(e) => updateWriteInTest(idx, 'testName', e.target.value)}
                        className="flex-1"
                      />
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => removeWriteInTest(idx)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </Paper>

              {/* ===== MICROBIOLOGY ===== */}
              {microTests.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    MICROBIOLOGY
                  </Text>
                  {renderSectionToggle('Microbiology', microTests.length)}
                  <Collapse in={expandedSections.has('Microbiology')}>
                    <div className="mr-4 ml-4">
                      <Group gap="md" mb="sm" mt="sm">
                        <Checkbox
                          label="Select All"
                          checked={allMicroSelected}
                          indeterminate={someMicroSelected}
                          onChange={(e) => toggleAllMicro(e.target.checked)}
                        />
                      </Group>
                      <Text size="xs" fw={600} mb="xs">
                        Source:
                      </Text>
                      <Group gap="md" mb="sm">
                        {LABCORP_MICROBIOLOGY_SOURCES.map((source) => (
                          <Checkbox
                            key={source}
                            label={source}
                            checked={form.values.microbiologySources.includes(source)}
                            onChange={() => toggleArrayItem('microbiologySources', source)}
                          />
                        ))}
                        <TextInput
                          placeholder="Other source..."
                          value={form.values.microbiologyOtherSource}
                          onChange={(e) =>
                            form.setFieldValue('microbiologyOtherSource', e.target.value)
                          }
                          size="xs"
                          w={160}
                        />
                      </Group>
                      {renderTestTable(microTests)}
                    </div>
                  </Collapse>
                </Paper>
              )}

              {/* ===== NUSWAB TESTS ===== */}
              {(testsByCategory.get('nuswab') || []).length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    NuSwab TESTS
                  </Text>
                  <Text size="xs" c="dimmed" mb="sm">
                    Check only one NuSwab test.
                  </Text>
                  <Table withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={40} />
                        <Table.Th w={80}>Code</Table.Th>
                        <Table.Th>Test Name</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(testsByCategory.get('nuswab') || []).map((test) => (
                        <Table.Tr key={test.code}>
                          <Table.Td>
                            <Checkbox
                              checked={form.values.selectedTests.includes(test.code)}
                              onChange={() => toggleNuSwab(test.code)}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" className="font-mono">
                              {test.code}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" fw={500}>
                              {test.name}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              )}
            </>
          )}

          {/* Step 1 navigation */}
          <Group justify="flex-end">
            <Button variant="default" onClick={onBack}>
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={() => setStep('facility')}
              disabled={totalSelectedCount === 0}
            >
              Next: Order Delivery
            </Button>
          </Group>
        </Stack>
      )}

      {/* ===== STEP 2: FACILITY ===== */}
      {step === 'facility' && (
        <LabFacilityStep
          patient={patient}
          vendor="labcorp"
          selectedFacilityId={form.values.selectedFacilityId}
          onSelectFacility={(id, facility) => {
            // Picking a branch is the other half of the either/or.
            setPatientChoosesLocation(false)
            form.setFieldValue('selectedFacilityId', id)
            if (facility) setSelectedFacility(facility)
          }}
          patientChoosesLocation={patientChoosesLocation}
          onPatientChoosesLocation={() => {
            setPatientChoosesLocation(true)
            setFaxDeliveryChosen(false)
            form.setFieldValue('selectedFacilityId', '')
            setSelectedFacility(null)
          }}
          onFaxDelivery={() => {
            setPatientChoosesLocation(false)
            setFaxDeliveryChosen(true)
          }}
          faxDeliveryChosen={faxDeliveryChosen}
          selectedFacilityFax={selectedFacility?.fax || undefined}
          canAdvance={canAdvanceFromFacility}
          onBack={() => setStep('tests')}
          onNext={() => setStep('details')}
        />
      )}

      {/* ===== STEP 3: DETAILS ===== */}
      {step === 'details' && (
        <Stack gap="lg">
          <DiagnosisCodesSection
            value={form.values.diagnosisText}
            defaultCodes={diagnosisDefaults}
            onChange={(v) => form.setFieldValue('diagnosisText', v)}
          />
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              CLINICAL INFORMATION / COMMENTS
            </Text>
            <Textarea
              placeholder="Enter any additional clinical information or comments..."
              value={form.values.clinicalComments}
              onChange={(e) => form.setFieldValue('clinicalComments', e.target.value)}
              rows={3}
            />
          </Paper>
          <SignatureSection
            signatureDataUrl={form.values.signatureDataUrl}
            signatureDate={form.values.signatureDate}
            savedSignature={signature.savedSignature}
            signatureModalOpen={signature.signatureModalOpen}
            onOpenModal={signature.openSignatureModal}
            onCloseModal={signature.closeSignatureModal}
            onConfirm={handleSignatureConfirm}
            onSaveSignature={signature.handleSaveSignature}
            certificationText="Please order only if medically necessary and appropriate for individual patients."
          />
          <Group justify="space-between">
            <Button variant="default" onClick={() => setStep('facility')}>
              Back
            </Button>
            <TooltipButton
              color="teal"
              disabled={submission.orderSubmitted || !form.values.signatureDataUrl}
              disabledReason={!form.values.signatureDataUrl ? SIGNATURE_REQUIRED_TOOLTIP : null}
              onClick={handleReviewOrder}
            >
              Review Order
            </TooltipButton>
          </Group>
        </Stack>
      )}

      {/* ===== STEP 4: REVIEW ===== */}
      {step === 'review' && (
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              ORDER SUMMARY
            </Text>
            <Text fw={500} size="sm" mb="xs">
              Facility
            </Text>
            {patientChoosesLocation ? (
              <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
                <Text fw={600} size="sm" c="blue">
                  Patient will choose a Labcorp location
                </Text>
                <Text size="xs" mt={4}>
                  No facility fax is required. The patient receives a printable requisition valid at
                  any Labcorp location offering the ordered test.
                </Text>
              </div>
            ) : selectedFacility ? (
              <div className="mb-4 rounded-md border border-gray-200 p-3">
                <Text fw={600} size="sm">
                  {selectedFacility.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {selectedFacility.address}, {selectedFacility.city}, {selectedFacility.state}{' '}
                  {selectedFacility.zip}
                </Text>
                {selectedFacility.fax && (
                  <Text size="xs" c="dimmed">
                    Fax: {selectedFacility.fax}
                  </Text>
                )}
              </div>
            ) : null}
          </Paper>
          <OrderReviewSummary
            vendorName="Labcorp"
            tests={cartItems}
            diagnosisText={form.values.diagnosisText}
            sections={reviewSections}
            signatureDataUrl={form.values.signatureDataUrl}
            signatureDate={form.values.signatureDate}
            submitLabel={patientChoosesLocation ? 'Submit & Save Requisition' : undefined}
            submittedLabel={patientChoosesLocation ? 'Requisition Saved' : undefined}
            onEdit={() => setStep('details')}
            onSubmit={handleSubmitOrder}
            onPreview={submission.handlePreviewPdf}
            submitting={submission.submitting}
            exporting={submission.exporting}
            orderSubmitted={submission.orderSubmitted}
            isPractitioner={isPractitioner}
            canOrderLoading={canOrderLoading}
            labError={submission.labError}
            orgError={orgError}
            mustSaveAsDraft={!!mustSaveAsDraft}
            draftNotice={draftOnlyReason({
              usingCoordinatorNote: false,
              facilityPendingApproval: !!facilityPendingApproval,
            })}
            onSaveDraft={handleSaveDraft}
          />
        </Stack>
      )}
      {coordinatorFlow.modal}
    </OrderFormLayout>
  )
}
